/**
 * Booking form definitions for each service category.
 * Each form has steps, and the Service Details step has Typeform-style questions.
 */

// Shared additional-info question appended to all categories
const additionalInfoQuestion = {
  id: "additionalInfo",
  type: "textarea",
  question: "Anything else your service provider should know?",
  reviewLabel: "Additional information",
  placeholder:
    "Any details that weren't covered above — the more info you provide, the more accurate your estimate will be.",
  required: false,
  maxLength: 500,
};

// Helper to build the standard step set for a category
function buildSteps(questions) {
  return [
    {
      id: "service-details",
      name: "Service Details",
      questions: [...questions, additionalInfoQuestion],
    },
    { id: "datetime", name: "Date & Time", component: "DateTimeStep" },
    { id: "address", name: "Service Address", component: "AddressStep" },
    { id: "estimate", name: "Price Estimate", component: "EstimateStep" },
  ];
}

export const bookingForms = {
  cleaning: {
    id: "cleaning",
    name: "House Cleaning",
    providerName: "cleaner",
    slug: "cleaning",
    steps: buildSteps([
      {
        id: "cleaning-type",
        type: "single-choice",
        question: "What type of cleaning do you need?",
        reviewLabel: "Cleaning type",
        required: true,
        options: [
          {
            value:
              "Standard cleaning (regular maintenance — dusting, vacuuming, mopping, bathrooms, kitchen surfaces)",
            label: "Standard cleaning",
            description: "Regular upkeep for a maintained home",
          },
          {
            value:
              "Deep cleaning (thorough top-to-bottom clean including baseboards, inside appliances, detailed scrubbing)",
            label: "Deep cleaning",
            description: "More thorough than a regular clean",
          },
          {
            value:
              "Move-in or move-out cleaning (comprehensive clean for a property transition)",
            label: "Move-in / move-out clean",
            description: "Preparing a home for new occupants",
          },
          {
            value:
              "Post-renovation cleaning (removing construction dust, debris, and residue)",
            label: "Post-renovation clean",
            description: "After construction or renovation work",
          },
        ],
      },
      {
        id: "home-size",
        type: "single-choice",
        question: "How big is your home?",
        reviewLabel: "Home size",
        required: true,
        options: [
          {
            value: "Studio or 1-bedroom apartment/condo (under 700 sq ft)",
            label: "Studio / 1-bedroom",
          },
          {
            value: "2-bedroom apartment or small home (700–1,200 sq ft)",
            label: "2-bedroom",
          },
          {
            value: "3-bedroom home (1,200–2,000 sq ft)",
            label: "3-bedroom",
          },
          {
            value: "4-bedroom home (2,000–3,000 sq ft)",
            label: "4-bedroom",
          },
          {
            value: "5+ bedroom or large home (over 3,000 sq ft)",
            label: "5+ bedrooms",
          },
        ],
      },
      {
        id: "num-bathrooms",
        type: "single-choice",
        question: "How many bathrooms does your home have?",
        reviewLabel: "Bathrooms",
        required: true,
        options: [
          { value: "1 bathroom", label: "1" },
          { value: "2 bathrooms", label: "2" },
          { value: "3 bathrooms", label: "3" },
          { value: "4 or more bathrooms", label: "4+" },
        ],
      },
      {
        id: "home-condition",
        type: "single-choice",
        question: "How would you describe the current condition of your home?",
        reviewLabel: "Home condition",
        required: true,
        options: [
          {
            value: "Well-maintained — regularly cleaned, no major buildup",
            label: "Well-maintained",
          },
          {
            value:
              "Moderately messy — some areas need extra attention, hasn't been deep cleaned in a while",
            label: "Needs some extra attention",
          },
          {
            value:
              "Very messy or neglected — significant buildup, hasn't been cleaned in months",
            label: "Needs a lot of work",
          },
        ],
      },
      {
        id: "pets",
        type: "single-choice",
        question: "Do you have pets?",
        reviewLabel: "Pets",
        required: true,
        options: [
          { value: "No pets", label: "No" },
          { value: "1–2 pets (cat or dog)", label: "Yes, 1–2" },
          { value: "3 or more pets", label: "Yes, 3+" },
        ],
      },
    ]),
  },

  plumbing: {
    id: "plumbing",
    name: "Plumbing",
    providerName: "plumber",
    slug: "plumbing",
    steps: buildSteps([
      {
        id: "plumbing-issue-type",
        type: "single-choice",
        question: "What do you need help with?",
        reviewLabel: "Issue type",
        required: true,
        options: [
          {
            value: "Fix a leaking or dripping faucet",
            label: "Leaking faucet",
          },
          {
            value: "Unclog a drain (sink, shower, bathtub, or floor drain)",
            label: "Clogged drain",
          },
          {
            value: "Fix or replace a toilet (running, leaking, or broken)",
            label: "Toilet issue",
          },
          {
            value: "Install or replace a faucet or fixture",
            label: "Install a faucet/fixture",
          },
          { value: "Fix a leaking or burst pipe", label: "Pipe leak" },
          {
            value: "Water heater repair or replacement",
            label: "Water heater issue",
          },
          {
            value: "Install or repair a garbage disposal",
            label: "Garbage disposal",
          },
          { value: "Sewer or main drain issue", label: "Sewer / main drain" },
          { value: "Other plumbing issue", label: "Other" },
        ],
      },
      {
        id: "plumbing-location",
        type: "single-choice",
        question: "Where in your home is the issue?",
        reviewLabel: "Location",
        required: true,
        options: [
          { value: "Kitchen", label: "Kitchen" },
          { value: "Bathroom", label: "Bathroom" },
          { value: "Laundry room", label: "Laundry room" },
          { value: "Basement", label: "Basement" },
          { value: "Outdoor or garage", label: "Outdoor / garage" },
          { value: "Multiple locations", label: "Multiple areas" },
          { value: "Not sure", label: "Not sure" },
        ],
      },
      {
        id: "plumbing-water-heater-type",
        type: "single-choice",
        question: "What type of water heater do you have?",
        reviewLabel: "Water heater type",
        required: true,
        showIf: {
          "plumbing-issue-type": "Water heater repair or replacement",
        },
        options: [
          { value: "Tank water heater (gas)", label: "Tank (gas)" },
          { value: "Tank water heater (electric)", label: "Tank (electric)" },
          { value: "Tankless water heater", label: "Tankless" },
          { value: "Not sure", label: "Not sure" },
        ],
      },
      {
        id: "plumbing-num-items",
        type: "single-choice",
        question: "How many fixtures or areas need work?",
        reviewLabel: "Number of fixtures",
        required: true,
        options: [
          { value: "Just 1 fixture or area", label: "1" },
          { value: "2–3 fixtures or areas", label: "2–3" },
          { value: "4 or more fixtures or areas", label: "4+" },
        ],
      },
    ]),
  },

  "furniture-assembly": {
    id: "furniture-assembly",
    name: "Furniture Assembly",
    providerName: "furniture assembler",
    slug: "furniture-assembly",
    steps: buildSteps([
      {
        id: "assembly-items",
        type: "multi-choice",
        question: "What do you need assembled?",
        reviewLabel: "Items",
        required: true,
        options: [
          { value: "Bookcase or shelving unit", label: "Bookcase / shelves" },
          {
            value: "Desk or office furniture",
            label: "Desk / office furniture",
          },
          { value: "Bed frame", label: "Bed frame" },
          { value: "Wardrobe or closet system", label: "Wardrobe / closet" },
          { value: "Dining table and/or chairs", label: "Dining furniture" },
          { value: "Baby crib or nursery furniture", label: "Crib / nursery" },
          {
            value: "Outdoor furniture (patio set, gazebo)",
            label: "Outdoor furniture",
          },
          {
            value: "Exercise equipment (treadmill, bike, etc.)",
            label: "Exercise equipment",
          },
          { value: "Other (please describe below)", label: "Other" },
        ],
      },
      {
        id: "assembly-quantity",
        type: "single-choice",
        question: "How many items need assembling?",
        reviewLabel: "Number of items",
        required: true,
        options: [
          { value: "1 item", label: "1" },
          { value: "2–3 items", label: "2–3" },
          { value: "4–5 items", label: "4–5" },
          { value: "6 or more items", label: "6+" },
        ],
      },
      {
        id: "assembly-brand",
        type: "single-choice",
        question: "Where is the furniture from?",
        reviewLabel: "Brand / store",
        required: false,
        options: [
          { value: "IKEA", label: "IKEA" },
          {
            value: "Wayfair, Amazon, or similar online retailer",
            label: "Wayfair / Amazon / online",
          },
          { value: "Other furniture store or brand", label: "Other store" },
          { value: "Not sure", label: "Not sure" },
        ],
      },
      {
        id: "assembly-disassembly",
        type: "single-choice",
        question: "Do you also need old furniture taken apart?",
        reviewLabel: "Disassembly needed",
        required: true,
        options: [
          { value: "No, just assembly", label: "Assembly only" },
          {
            value: "Yes, I need old furniture disassembled too",
            label: "Yes, disassembly too",
          },
        ],
      },
    ]),
  },

  "drywall-repair": {
    id: "drywall-repair",
    name: "Drywall Repair",
    providerName: "drywall specialist",
    slug: "drywall-repair",
    steps: buildSteps([
      {
        id: "drywall-damage-type",
        type: "multi-choice",
        question: "What kind of drywall damage do you have?",
        reviewLabel: "Damage type",
        required: true,
        options: [
          {
            value: "Small holes (nail holes, screw holes, small dents)",
            label: "Small holes",
          },
          {
            value: "Medium hole (fist-sized, 2–6 inches)",
            label: "Medium hole",
          },
          {
            value: "Large hole or damaged section (bigger than 6 inches)",
            label: "Large hole / section",
          },
          { value: "Cracks or separating seams", label: "Cracks / seams" },
          {
            value: "Water damage (staining, bubbling, soft spots)",
            label: "Water damage",
          },
          { value: "Other", label: "Other" },
        ],
      },
      {
        id: "drywall-num-spots",
        type: "single-choice",
        question: "How many areas need repair?",
        reviewLabel: "Number of areas",
        required: true,
        options: [
          { value: "1–2 spots", label: "1–2" },
          { value: "3–5 spots", label: "3–5" },
          { value: "6 or more spots", label: "6+" },
        ],
      },
      {
        id: "drywall-location",
        type: "single-choice",
        question: "Where is the damage?",
        reviewLabel: "Location",
        required: true,
        options: [
          {
            value: "Wall (standard height, easy to reach)",
            label: "Wall",
          },
          { value: "Ceiling", label: "Ceiling" },
          { value: "Both wall and ceiling", label: "Both" },
          {
            value: "Stairwell or high wall (hard to reach)",
            label: "High / hard to reach",
          },
        ],
      },
      {
        id: "drywall-finish",
        type: "single-choice",
        question: "What level of finish do you need?",
        reviewLabel: "Finish level",
        required: true,
        options: [
          {
            value: "Just patch it up — doesn't need to look perfect",
            label: "Patch only",
            description: "Functional repair",
          },
          {
            value: "Patch and match the existing wall texture",
            label: "Patch + texture match",
            description: "Blends with surrounding wall",
          },
          {
            value: "Patch, texture, and paint to match — make it invisible",
            label: "Full finish (patch + texture + paint)",
            description: "Seamless result",
          },
        ],
      },
    ]),
  },

  "interior-painting": {
    id: "interior-painting",
    name: "Interior Painting",
    providerName: "painter",
    slug: "interior-painting",
    steps: buildSteps([
      {
        id: "painting-scope",
        type: "multi-choice",
        question: "What do you need painted?",
        reviewLabel: "Scope",
        required: true,
        options: [
          { value: "One room", label: "1 room" },
          { value: "Two rooms", label: "2 rooms" },
          { value: "Three or more rooms", label: "3+ rooms" },
          { value: "Accent wall only", label: "Accent wall" },
          { value: "Touch-ups or small areas", label: "Touch-ups" },
          { value: "Hallway or stairwell", label: "Hallway / stairwell" },
        ],
      },
      {
        id: "painting-surfaces",
        type: "single-choice",
        question: "What surfaces need painting?",
        reviewLabel: "Surfaces",
        required: true,
        options: [
          { value: "Walls only", label: "Walls only" },
          { value: "Walls and ceiling", label: "Walls + ceiling" },
          {
            value: "Walls, ceiling, and trim (baseboards, door frames)",
            label: "Walls + ceiling + trim",
          },
          { value: "Not sure", label: "Not sure" },
        ],
      },
      {
        id: "painting-colour-change",
        type: "single-choice",
        question: "Are you changing the wall colour?",
        reviewLabel: "Colour change",
        required: true,
        options: [
          {
            value: "Same colour or similar shade (touch-up / refresh)",
            label: "Same or similar colour",
          },
          {
            value: "Light to light or light to dark",
            label: "Changing colour (light base)",
          },
          {
            value: "Dark to light (significant colour change)",
            label: "Dark to light",
            description: "May need primer + extra coats",
          },
          { value: "Not sure yet", label: "Not sure" },
        ],
      },
      {
        id: "painting-ceiling-height",
        type: "single-choice",
        question: "What are your ceiling heights?",
        reviewLabel: "Ceiling height",
        required: true,
        options: [
          { value: "Standard height (8 feet)", label: "Standard (8 ft)" },
          { value: "Slightly tall (9–10 feet)", label: "9–10 ft" },
          {
            value: "High or vaulted ceilings (over 10 feet)",
            label: "Over 10 ft / vaulted",
          },
        ],
      },
      {
        id: "painting-prep",
        type: "single-choice",
        question: "Do the walls need any prep work?",
        reviewLabel: "Prep work",
        required: false,
        options: [
          {
            value: "No — walls are in good condition, ready to paint",
            label: "No prep needed",
          },
          {
            value: "Some minor patching or sanding needed",
            label: "Minor prep",
          },
          {
            value: "Wallpaper needs to be removed first",
            label: "Wallpaper removal",
          },
          { value: "Not sure", label: "Not sure" },
        ],
      },
    ]),
  },

  "general-home-repairs": {
    id: "general-home-repairs",
    name: "General Home Repairs",
    providerName: "handyman",
    slug: "general-home-repairs",
    steps: buildSteps([
      {
        id: "repairs-task-type",
        type: "multi-choice",
        question: "What do you need done?",
        reviewLabel: "Tasks",
        required: true,
        options: [
          {
            value: "Hang shelves, pictures, or mirrors",
            label: "Hanging / mounting items",
          },
          {
            value: "Install curtain rods or blinds",
            label: "Curtain rods / blinds",
          },
          {
            value: "Door repair, adjustment, or installation",
            label: "Door work",
          },
          {
            value: "Caulking or sealing (bathroom, kitchen, windows)",
            label: "Caulking / sealing",
          },
          {
            value: "Weather stripping (doors or windows)",
            label: "Weather stripping",
          },
          {
            value: "Cabinet or drawer repair",
            label: "Cabinet / drawer repair",
          },
          {
            value: "Install bathroom accessories (towel bars, grab bars)",
            label: "Bathroom accessories",
          },
          { value: "Fence or deck repair", label: "Fence / deck repair" },
          { value: "Gutter cleaning", label: "Gutter cleaning" },
          { value: "Power washing", label: "Power washing" },
          { value: "Other (please describe below)", label: "Other" },
        ],
      },
      {
        id: "repairs-num-tasks",
        type: "single-choice",
        question: "How many tasks do you need done?",
        reviewLabel: "Number of tasks",
        required: true,
        options: [
          { value: "Just 1 task", label: "1 task" },
          { value: "2–3 tasks", label: "2–3 tasks" },
          { value: "4 or more tasks", label: "4+ tasks" },
        ],
      },
      {
        id: "repairs-materials",
        type: "single-choice",
        question: "Do you have the materials or parts needed?",
        reviewLabel: "Materials provided",
        required: true,
        options: [
          { value: "Yes, I have everything needed", label: "Yes" },
          {
            value: "No, they'll need to bring or buy materials",
            label: "No",
          },
          { value: "Not sure what's needed", label: "Not sure" },
        ],
      },
      {
        id: "repairs-location",
        type: "single-choice",
        question: "Is the work indoors or outdoors?",
        reviewLabel: "Location",
        required: true,
        options: [
          { value: "Indoor", label: "Indoor" },
          { value: "Outdoor", label: "Outdoor" },
          { value: "Both", label: "Both" },
        ],
      },
    ]),
  },

  "furnace-heating": {
    id: "furnace-heating",
    name: "Furnace / Heating",
    providerName: "HVAC technician",
    slug: "furnace-heating",
    steps: buildSteps([
      {
        id: "furnace-service-type",
        type: "single-choice",
        question: "What do you need help with?",
        reviewLabel: "Service type",
        required: true,
        options: [
          {
            value: "Annual maintenance or tune-up",
            label: "Maintenance / tune-up",
            description: "Preventive inspection and cleaning",
          },
          {
            value: "My furnace is not working properly or has stopped working",
            label: "Furnace repair",
            description: "Something is wrong with the heating",
          },
          {
            value:
              "Thermostat issue (not working, inaccurate, or want to upgrade)",
            label: "Thermostat issue",
          },
          {
            value: "Strange noises, smells, or performance issues",
            label: "Diagnosis / inspection",
          },
          { value: "Other heating issue", label: "Other" },
        ],
      },
      {
        id: "furnace-type",
        type: "single-choice",
        question: "What type of furnace do you have?",
        reviewLabel: "Furnace type",
        required: true,
        options: [
          { value: "Gas furnace (most common)", label: "Gas furnace" },
          { value: "Electric furnace", label: "Electric furnace" },
          {
            value: "Boiler (hot water or steam radiators)",
            label: "Boiler",
          },
          { value: "Heat pump", label: "Heat pump" },
          { value: "Not sure", label: "Not sure" },
        ],
      },
      {
        id: "furnace-age",
        type: "single-choice",
        question: "Roughly how old is your furnace?",
        reviewLabel: "Furnace age",
        required: true,
        options: [
          { value: "Less than 5 years old", label: "Under 5 years" },
          { value: "5–10 years old", label: "5–10 years" },
          { value: "10–15 years old", label: "10–15 years" },
          { value: "Over 15 years old", label: "Over 15 years" },
          { value: "Not sure", label: "Not sure" },
        ],
      },
      {
        id: "furnace-symptoms",
        type: "multi-choice",
        question: "What are you experiencing?",
        reviewLabel: "Symptoms",
        required: false,
        showIf: {
          "furnace-service-type": [
            "My furnace is not working properly or has stopped working",
            "Thermostat issue (not working, inaccurate, or want to upgrade)",
            "Strange noises, smells, or performance issues",
          ],
        },
        options: [
          {
            value: "Furnace is not producing any heat",
            label: "No heat at all",
          },
          {
            value: "Furnace runs but home isn't getting warm enough",
            label: "Weak heat",
          },
          {
            value: "Furnace turns on and off frequently (short cycling)",
            label: "Keeps cycling on/off",
          },
          {
            value: "Making unusual noises (banging, squealing, rattling)",
            label: "Strange noises",
          },
          {
            value: "Unusual smell (burning, musty)",
            label: "Strange smell",
          },
          {
            value: "Higher than normal energy bills",
            label: "High energy bills",
          },
          {
            value: "Pilot light or ignition issues",
            label: "Ignition problems",
          },
          {
            value: "Thermostat not responding correctly",
            label: "Thermostat issues",
          },
        ],
      },
      {
        id: "furnace-last-maintenance",
        type: "single-choice",
        question: "When was the furnace last serviced?",
        reviewLabel: "Last serviced",
        required: false,
        options: [
          { value: "Within the last year", label: "Within 1 year" },
          { value: "1–3 years ago", label: "1–3 years ago" },
          {
            value: "More than 3 years ago or never",
            label: "3+ years or never",
          },
          { value: "Not sure", label: "Not sure" },
        ],
      },
    ]),
  },

  "air-conditioning": {
    id: "air-conditioning",
    name: "Air Conditioning",
    providerName: "HVAC technician",
    slug: "air-conditioning",
    steps: buildSteps([
      {
        id: "ac-service-type",
        type: "single-choice",
        question: "What do you need help with?",
        reviewLabel: "Service type",
        required: true,
        options: [
          {
            value: "Annual AC maintenance or tune-up",
            label: "Maintenance / tune-up",
          },
          {
            value:
              "My air conditioner is not cooling properly or has stopped working",
            label: "AC repair",
          },
          {
            value: "Thermostat issue related to cooling",
            label: "Thermostat issue",
          },
          {
            value: "Strange noises, smells, or water leaking from the AC",
            label: "Diagnosis / inspection",
          },
          { value: "Other AC issue", label: "Other" },
        ],
      },
      {
        id: "ac-system-type",
        type: "single-choice",
        question: "What type of cooling system do you have?",
        reviewLabel: "System type",
        required: true,
        options: [
          {
            value: "Central air conditioning (connected to furnace/ductwork)",
            label: "Central AC",
          },
          { value: "Ductless mini-split", label: "Ductless mini-split" },
          {
            value: "Window or portable AC unit",
            label: "Window / portable unit",
          },
          { value: "Not sure", label: "Not sure" },
        ],
      },
      {
        id: "ac-age",
        type: "single-choice",
        question: "Roughly how old is your AC unit?",
        reviewLabel: "AC age",
        required: true,
        options: [
          { value: "Less than 5 years old", label: "Under 5 years" },
          { value: "5–10 years old", label: "5–10 years" },
          { value: "10–15 years old", label: "10–15 years" },
          { value: "Over 15 years old", label: "Over 15 years" },
          { value: "Not sure", label: "Not sure" },
        ],
      },
      {
        id: "ac-symptoms",
        type: "multi-choice",
        question: "What are you experiencing?",
        reviewLabel: "Symptoms",
        required: false,
        showIf: {
          "ac-service-type": [
            "My air conditioner is not cooling properly or has stopped working",
            "Thermostat issue related to cooling",
            "Strange noises, smells, or water leaking from the AC",
          ],
        },
        options: [
          {
            value: "AC is running but not cooling the home",
            label: "Running but not cooling",
          },
          { value: "AC is not turning on at all", label: "Won't turn on" },
          {
            value: "AC turns on and off frequently",
            label: "Short cycling",
          },
          {
            value: "Strange noises from the indoor or outdoor unit",
            label: "Strange noises",
          },
          {
            value: "Water leaking or pooling around the unit",
            label: "Water leak",
          },
          {
            value: "Ice forming on the unit or refrigerant lines",
            label: "Ice buildup",
          },
          { value: "Bad smell coming from the vents", label: "Bad smell" },
          {
            value: "Higher than normal energy bills",
            label: "High energy bills",
          },
        ],
      },
      {
        id: "ac-last-maintenance",
        type: "single-choice",
        question: "When was the AC last serviced?",
        reviewLabel: "Last serviced",
        required: false,
        options: [
          { value: "Within the last year", label: "Within 1 year" },
          { value: "1–3 years ago", label: "1–3 years ago" },
          {
            value: "More than 3 years ago or never",
            label: "3+ years or never",
          },
          { value: "Not sure", label: "Not sure" },
        ],
      },
    ]),
  },

  electrical: {
    id: "electrical",
    name: "Electrical",
    providerName: "electrician",
    slug: "electrical",
    steps: buildSteps([
      {
        id: "electrical-task-type",
        type: "multi-choice",
        question: "What electrical work do you need done?",
        reviewLabel: "Work needed",
        required: true,
        options: [
          {
            value: "Replace or install light switches or outlets",
            label: "Switches / outlets",
          },
          {
            value: "Install or replace a light fixture",
            label: "Light fixture",
          },
          { value: "Install a ceiling fan", label: "Ceiling fan" },
          {
            value:
              "Troubleshoot an electrical problem (tripping breaker, flickering lights, dead outlet)",
            label: "Troubleshooting",
          },
          {
            value:
              "Install a dedicated circuit for an appliance (dryer, oven, hot tub)",
            label: "Dedicated circuit",
          },
          {
            value: "Electrical panel inspection or upgrade",
            label: "Panel work",
          },
          { value: "Install an EV charger", label: "EV charger" },
          { value: "Outdoor or landscape lighting", label: "Outdoor lighting" },
          { value: "Other electrical work", label: "Other" },
        ],
      },
      {
        id: "electrical-quantity",
        type: "single-choice",
        question: "How many items or areas need work?",
        reviewLabel: "Number of items",
        required: true,
        options: [
          { value: "1 item", label: "1" },
          { value: "2–3 items", label: "2–3" },
          { value: "4–6 items", label: "4–6" },
          { value: "7 or more items", label: "7+" },
        ],
      },
      {
        id: "electrical-new-or-replace",
        type: "single-choice",
        question:
          "Is this replacing something existing, or adding something new?",
        reviewLabel: "New or replacement",
        required: true,
        options: [
          {
            value: "Replacing or upgrading existing fixtures/switches/outlets",
            label: "Replacing existing",
          },
          {
            value: "Adding new where there are none currently",
            label: "Adding new",
          },
          { value: "Mix of both", label: "Mix of both" },
          { value: "Not sure", label: "Not sure" },
        ],
      },
      {
        id: "electrical-wall-type",
        type: "single-choice",
        question: "What are your walls made of?",
        reviewLabel: "Wall type",
        required: false,
        showIf: {
          "electrical-new-or-replace": [
            "Adding new where there are none currently",
            "Mix of both",
          ],
        },
        options: [
          {
            value: "Finished drywall (standard painted walls)",
            label: "Drywall",
          },
          {
            value: "Open or unfinished (exposed studs, unfinished basement)",
            label: "Unfinished",
          },
          { value: "Plaster (older home)", label: "Plaster" },
          { value: "Not sure", label: "Not sure" },
        ],
      },
    ]),
  },

  "tv-mounting": {
    id: "tv-mounting",
    name: "TV Mounting",
    providerName: "TV installer",
    slug: "tv-mounting",
    steps: buildSteps([
      {
        id: "tv-size",
        type: "single-choice",
        question: "What size is your TV?",
        reviewLabel: "TV size",
        required: true,
        options: [
          { value: "Small TV (under 43 inches)", label: 'Under 43"' },
          { value: "Medium TV (43–54 inches)", label: '43–54"' },
          { value: "Large TV (55–65 inches)", label: '55–65"' },
          { value: "Extra large TV (over 65 inches)", label: 'Over 65"' },
        ],
      },
      {
        id: "tv-wall-type",
        type: "single-choice",
        question: "What type of wall will the TV be mounted on?",
        reviewLabel: "Wall type",
        required: true,
        options: [
          { value: "Drywall (standard interior wall)", label: "Drywall" },
          { value: "Brick or stone", label: "Brick / stone" },
          { value: "Concrete", label: "Concrete" },
          { value: "Plaster (older home)", label: "Plaster" },
          { value: "Not sure", label: "Not sure" },
        ],
      },
      {
        id: "tv-mount-type",
        type: "single-choice",
        question: "What type of mount do you want?",
        reviewLabel: "Mount type",
        required: true,
        options: [
          {
            value: "Fixed mount (TV sits flat against the wall, no movement)",
            label: "Fixed",
          },
          {
            value: "Tilting mount (can angle TV up or down)",
            label: "Tilting",
          },
          {
            value: "Full-motion mount (swivels, tilts, and extends from wall)",
            label: "Full-motion",
          },
          {
            value: "Not sure — I'd like a recommendation",
            label: "Not sure",
          },
        ],
      },
      {
        id: "tv-placement",
        type: "single-choice",
        question: "Where will the TV be mounted?",
        reviewLabel: "Placement",
        required: true,
        options: [
          {
            value: "Standard wall at normal viewing height",
            label: "Standard wall",
          },
          { value: "Above a fireplace", label: "Above fireplace" },
          { value: "In a corner", label: "Corner" },
          { value: "High on the wall (bedroom, gym)", label: "High mount" },
          { value: "Ceiling mount", label: "Ceiling" },
        ],
      },
      {
        id: "tv-cable-management",
        type: "single-choice",
        question: "How would you like the cables handled?",
        reviewLabel: "Cable management",
        required: true,
        options: [
          {
            value: "Cables visible (no cable management needed)",
            label: "No cable management",
          },
          {
            value:
              "Surface cord cover (cables hidden behind a track on the wall)",
            label: "Surface cord cover",
          },
          {
            value: "In-wall cable concealment (cables run inside the wall)",
            label: "In-wall concealment",
          },
        ],
      },
      {
        id: "tv-mount-provided",
        type: "single-choice",
        question: "Do you already have the TV mount bracket?",
        reviewLabel: "Mount bracket",
        required: true,
        options: [
          {
            value: "Yes, I already have the mount bracket",
            label: "Yes",
          },
          {
            value: "No, I need the installer to provide one",
            label: "No, please provide one",
          },
          { value: "I'm not sure what I need", label: "Not sure" },
        ],
      },
    ]),
  },
};

/**
 * Get form definition for a service
 */
export function getBookingForm(serviceSlug) {
  return bookingForms[serviceSlug] || null;
}

/**
 * Check if a question should be shown based on previous answers.
 * Supports single-value match and array-of-values (OR) match.
 * Also handles multi-choice answers (arrays) by checking for intersection.
 */
export function shouldShowQuestion(question, answers) {
  if (!question.showIf) return true;

  for (const [questionId, expectedValue] of Object.entries(question.showIf)) {
    const answer = answers[questionId];
    if (answer === undefined) return false;

    if (Array.isArray(expectedValue)) {
      // expectedValue is an array — answer must match at least one
      if (Array.isArray(answer)) {
        if (!answer.some((a) => expectedValue.includes(a))) return false;
      } else {
        if (!expectedValue.includes(answer)) return false;
      }
    } else {
      // expectedValue is a single string
      if (Array.isArray(answer)) {
        if (!answer.includes(expectedValue)) return false;
      } else {
        if (answer !== expectedValue) return false;
      }
    }
  }

  return true;
}

/**
 * Format a question ID into a human-readable label (fallback when form definition is unavailable).
 * Handles both kebab-case ("cleaning-type") and camelCase ("additionalInfo").
 */
function formatQuestionId(id) {
  return id
    .replace(/[-_]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^\w/, (c) => c.toUpperCase());
}

/**
 * Resolve a booking's raw `details` object into structured display entries.
 *
 * @param {string|null} serviceSlug - The service category slug (e.g. "cleaning")
 * @param {Object} details - The raw { questionId: answerValue } object
 * @returns {Array<{ question: string, answer: string }>}
 */
export function resolveDetailsForDisplay(serviceSlug, details) {
  if (!details || typeof details !== "object") return [];

  const form = serviceSlug ? getBookingForm(serviceSlug) : null;
  const questions =
    form?.steps?.find((s) => s.id === "service-details")?.questions || [];

  const result = [];
  const seen = new Set();

  // Walk form questions in order, filtering with shouldShowQuestion
  // to avoid displaying stale answers from conditional questions
  for (const questionDef of questions) {
    if (!(questionDef.id in details)) continue;
    if (!shouldShowQuestion(questionDef, details)) continue;

    seen.add(questionDef.id);
    result.push(
      resolveEntry(questionDef.id, details[questionDef.id], questionDef),
    );
  }

  // Append any unknown keys not covered by the form definition
  for (const [key, value] of Object.entries(details)) {
    if (seen.has(key)) continue;
    result.push(resolveEntry(key, value, null));
  }

  return result;
}

function resolveEntry(key, value, questionDef) {
  const question =
    questionDef?.reviewLabel || questionDef?.question || formatQuestionId(key);

  let answer;
  if (questionDef?.options && !Array.isArray(value)) {
    const match = questionDef.options.find((o) => o.value === value);
    answer = match?.label || String(value);
  } else if (questionDef?.options && Array.isArray(value)) {
    answer = value
      .map((v) => {
        const match = questionDef.options.find((o) => o.value === v);
        return match?.label || String(v);
      })
      .join(", ");
  } else {
    answer = String(value);
  }

  return { question, answer };
}
